type StorageName = 'localStorage' | 'sessionStorage';

// All localStorage entries are persisted across cookie chunks to avoid the 431
// (Request Header Fields Too Large) error that occurs when each key gets its
// own cookie. Data is split across __ls__0, __ls__1, etc. to stay within the
// per-cookie 4KB limit while supporting larger payloads (e.g. Okta JWTs).
//
// We use document.cookie directly (not js-cookie) to avoid any encoding quirks
// on STBs. Values are encodeURIComponent'd before writing so chunk sizes are
// predictable.
const LS_COOKIE_PREFIX = '__ls__';
const MAX_CHUNK_BYTES = 3800; // exact encoded bytes per cookie, safely under 4 KB
const MAX_CHUNKS = 4; // 4 × 3800 = 15.2 KB, fits within nginx's 16 KB header limit
const COOKIE_EXPIRY_DAYS = 365;

function getChunkName(index: number): string {
  return `${LS_COOKIE_PREFIX}${index}`;
}

function getRawCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? match[1] : undefined;
}

function setRawCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/`;
}

function removeRawCookie(name: string) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
}

function writeChunkedCookie(serialized: string) {
  const encoded = encodeURIComponent(serialized);
  const totalChunks = Math.ceil(encoded.length / MAX_CHUNK_BYTES);

  if (totalChunks > MAX_CHUNKS) {
    console.warn(
      `[storage] Data too large for cookie persistence (${encoded.length} encoded bytes, max ${MAX_CHUNKS * MAX_CHUNK_BYTES}). Some data may not persist.`,
    );
    return;
  }

  for (let i = 0; i < totalChunks; i++) {
    const chunk = encoded.slice(i * MAX_CHUNK_BYTES, (i + 1) * MAX_CHUNK_BYTES);
    setRawCookie(getChunkName(i), chunk, COOKIE_EXPIRY_DAYS);
  }

  // Remove any leftover chunks from a previous larger payload.
  for (let i = totalChunks; i < MAX_CHUNKS; i++) {
    if (getRawCookie(getChunkName(i)) !== undefined) {
      removeRawCookie(getChunkName(i));
    }
  }
}

// Keys matching these prefixes are NOT persisted to cookies. They contain
// vendor/analytics config that gets fetched fresh on every launch and would
// otherwise consume the entire cookie budget (Conviva config alone is ~7 KB).
const PERSIST_BLACKLIST_PREFIXES = [
  'Conviva',
  'conviva',
  'adb_',
  'convivaOutQueue',
  'ConvivaSampling',
  '_dd',
];

function shouldPersist(key: string): boolean {
  return !PERSIST_BLACKLIST_PREFIXES.some((prefix) => key.startsWith(prefix));
}

// On Xi6 Flex STBs, window.localStorage is null. The inline shim
// (public/storage-shim.js) installs an in-memory Storage object and hydrates
// it from __ls__* cookies before any modules load. This module attaches the
// persistence callback so writes to the shim are backed by cookies.
//
// On platforms with native localStorage, isStorageAvailable returns true and
// this file is effectively a no-op.
function isStorageAvailable(name: StorageName): boolean {
  if (!Object.prototype.hasOwnProperty.call(globalThis, name) || globalThis[name] === null) {
    return false;
  }

  // The inline shim marks itself with __shim — it needs the persist callback.
  if ((globalThis[name] as Record<string, unknown>).__shim) {
    return false;
  }

  try {
    const testKey = `test-${name}-ocd`;
    globalThis[name].setItem(testKey, 'test');
    globalThis[name].removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

// Attach cookie persistence to the inline shim's localStorage if present.
// sessionStorage doesn't need persistence (session data doesn't survive restarts).
if (!isStorageAvailable('localStorage') && (globalThis.localStorage as any)?.__shim) {
  (window as any).__storagePersist = (store: Record<string, string>) => {
    try {
      const obj: Record<string, string> = {};
      for (const key of Object.keys(store)) {
        if (shouldPersist(key)) {
          obj[key] = store[key];
        }
      }
      writeChunkedCookie(JSON.stringify(obj));
    } catch (e) {
      console.warn('[storage] Cookie write failed:', e);
    }
  };
}
